///
/// EarthBrowser
///
/// Created by Matt Giger on 1/7/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { wgs } from "@/earthbrowser/utils/geographic"
import { cube } from "@stovokor/math/cube"
import { trig } from "@stovokor/math/math"
import { type sphere } from "@stovokor/math/sphere"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { type EarthTileIndexBuffer } from "./EarthTileGeometry"
import { type WebMercatorTileId } from "./WebMercatorTileId"

const cMeshResolution = [128,64,64,64,64,64,64,64,64,64,64,64,64,64,32,32,32]

export class WebMercatorOverlayTileGeometry implements Geometry {
	public tileId: WebMercatorTileId

	public coordinate: WebMercatorOverlayTileVertexBuffer
	public indexBuffer: EarthTileIndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds: cube = new cube(-wgs.a / 2, wgs.a / 2)
	public boundingSphere: sphere = this.bounds.sphere
	public available: boolean = true

	constructor(context: EarthBrowserContext, tileId: WebMercatorTileId) {
		this.tileId = tileId
		const resolution = cMeshResolution[tileId.z] ?? 32
		this.coordinate = new WebMercatorOverlayTileVertexBuffer(context, tileId, resolution)
		this.indexBuffer = context.indexBuffer(1, resolution)
		this.vertexBuffer = new VertexBuffer(context, [this.coordinate])
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
	}
}

class WebMercatorOverlayTileVertexBuffer extends VertexAttribute {
	constructor(context: StovokorContext, tileId: WebMercatorTileId, resolution: number) {
		super(context, 2)
		const verts = this.values

		const n = 1 << tileId.z
		const tileX0 = tileId.x / n
		const tileY0 = tileId.y / n
		const tileDX = 1 / n
		const tileDY = 1 / n

		// Generate vertices aligned to WebMercator's linear tile space:
		// - longitude is linear in x
		// - latitude is inverse-mercator of y
		// This avoids texture shear that can happen if we step linearly in latitude.
		for (let i = 0; i <= resolution; i++) {
			const tv = i / resolution
			const v = tileY0 + tv * tileDY

			// inverse mercator:
			// lat = 2*atan(exp((0.5 - v)*2π)) - π/2
			const lat = 2 * Math.atan(Math.exp((0.5 - v) * trig.two_pi)) - trig.half_pi

			for (let j = 0; j <= resolution; j++) {
				const tu = j / resolution
				const u = tileX0 + tu * tileDX
				const lon = u * trig.two_pi - trig.pi
				verts.push(lon, lat)
			}
		}
	}
}


